// §4.2 Pricing tab — the distributor ELIGIBLE LIST editor (Module 09 §2.5,
// M09-S02's add step). Visible only for tier='distributor' customers (the
// caller gates that); the §3.2 engine resolves distributor rates ONLY for
// products on this list — everything else prices at b2b with the §9.5
// fallback chip, which is what the empty state says in words.
//
// The ADD step resolves the product by EXACT SKU match through the catalogue
// search (the remnants.tsx itemMatch idiom): a substring that silently added
// the wrong product would put a distributor rate on an item nobody granted,
// so the confirm control arms only on an exact match, with pending/no-match
// badges while it is not one.
//
// Controls are offered only to `sales.tier.manage` holders (ga/fa mirror);
// the service refuses everyone else regardless (D-040 two-layer).

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { Card } from "../../../../components/card";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Field } from "../../../../components/field";
import { RefLabel } from "../../../../components/ref-label";
import { formatTimestamp } from "../../../../lib/datetime";
import { useProductIndex, useProductSearch } from "../../production/hooks/production-queries";
import { MutationFailureNote } from "../mutation-failure-note";
import {
  useAddDistributorProduct,
  useRemoveDistributorProduct,
  type CustomerDistributorProduct,
} from "../hooks/sales-queries";
import { bookClientPager } from "../hooks/book-pages";

export function DistributorProducts({
  customerId,
  rows,
  canManage,
}: {
  customerId: string;
  rows: readonly CustomerDistributorProduct[];
  canManage: boolean;
}) {
  const { t, i18n } = useTranslation();
  const [addingOpen, setAddingOpen] = useState(false);
  const [skuText, setSkuText] = useState("");
  const [note, setNote] = useState("");

  const products = useProductIndex(rows.map((row) => row.product_id));
  const search = useProductSearch(skuText.trim());
  const match = useMemo(() => {
    const needle = skuText.trim().toLowerCase();
    if (needle.length === 0) return undefined;
    return (search.data?.items ?? []).find((p) => p.sku.toLowerCase() === needle);
  }, [search.data, skuText]);

  const add = useAddDistributorProduct();
  const remove = useRemoveDistributorProduct();

  const columns: DataTableColumn<CustomerDistributorProduct>[] = [
    {
      key: "product_id",
      header: t("sales.eligible.colProduct"),
      id: true,
      render: (row) => {
        const ref = products.byId.get(row.product_id);
        return (
          <RefLabel
            value={ref !== undefined ? `${ref.sku} · ${ref.name}` : undefined}
            id={row.product_id}
            pending={products.isPending}
          />
        );
      },
    },
    {
      key: "note",
      header: t("sales.eligible.colNote"),
      render: (row) => <RefLabel value={row.note ?? undefined} id={row.note} />,
    },
    {
      key: "created_at",
      header: t("sales.eligible.colAdded"),
      age: true,
      render: (row) => formatTimestamp(row.created_at, i18n.language),
    },
    {
      key: "actions",
      header: t("sales.eligible.colActions"),
      actions: true,
      render: (row) =>
        canManage ? (
          <Button
            size="sm"
            variant="danger"
            data-testid="sales-eligible-remove"
            disabled={remove.isPending}
            onClick={() => {
              add.reset();
              remove.mutate({ customerId, productId: row.product_id });
            }}
          >
            {t("sales.eligible.remove")}
          </Button>
        ) : null,
    },
  ];

  return (
    <Card
      className="sales-eligible"
      data-testid="sales-eligible"
      title={t("sales.eligible.title")}
      count={rows.length}
      action={
        canManage ? (
          <Button
            size="sm"
            data-testid="sales-eligible-add"
            aria-expanded={addingOpen}
            onClick={() => {
              setAddingOpen(!addingOpen);
            }}
          >
            {addingOpen ? t("sales.common.close") : t("sales.eligible.add")}
          </Button>
        ) : undefined
      }
    >
      {rows.length === 0 ? (
        <p className="sales-hint" data-testid="sales-eligible-empty">
          {t("sales.eligible.empty")}
        </p>
      ) : (
        <DataTable
          clientPager={bookClientPager(t, i18n.language)}
          data-testid="sales-eligible-table"
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          empty={null}
          caption={t("sales.eligible.tableCaption")}
          rowTestId="sales-eligible-row"
          rowProps={(row) => ({
            "data-product-sku": products.byId.get(row.product_id)?.sku,
          })}
        />
      )}

      {add.isError ? (
        <MutationFailureNote
          data-testid="sales-mutation-failed"
          text={t("sales.eligible.addFailed")}
          error={add.error}
        />
      ) : null}
      {remove.isError ? (
        <MutationFailureNote
          data-testid="sales-mutation-failed"
          text={t("sales.eligible.removeFailed")}
          error={remove.error}
        />
      ) : null}

      {canManage && addingOpen ? (
        <div className="sales-channel-add">
          <Field label={t("sales.eligible.skuLabel")} wide>
            <input
              value={skuText}
              placeholder={t("sales.eligible.skuHint")}
              data-testid="sales-eligible-product-input"
              onChange={(e) => {
                setSkuText(e.target.value);
              }}
            />
            {skuText.trim().length > 0 ? (
              match !== undefined ? (
                <span className="sales-applied" data-testid="sales-eligible-match">
                  {t("sales.eligible.skuApplied", { sku: match.sku, name: match.name })}
                </span>
              ) : (
                <span className="sales-applied" data-testid="sales-eligible-no-match">
                  {t("sales.eligible.skuNoMatch")}
                </span>
              )
            ) : null}
          </Field>
          <Field label={t("sales.eligible.note")} wide>
            <input
              value={note}
              data-testid="sales-eligible-note"
              onChange={(e) => {
                setNote(e.target.value);
              }}
            />
          </Field>
          <Button
            variant="primary"
            size="sm"
            data-testid="sales-eligible-confirm"
            disabled={add.isPending || match === undefined}
            onClick={() => {
              if (match === undefined) return;
              remove.reset();
              add.mutate(
                { customerId, productId: match.id, note: note.trim() },
                {
                  onSuccess: () => {
                    setSkuText("");
                    setNote("");
                    setAddingOpen(false);
                  },
                },
              );
            }}
          >
            {t("sales.eligible.confirm")}
          </Button>
        </div>
      ) : null}
    </Card>
  );
}
